import { useMemo, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import {
  exceptionAssetsInTote,
  isExceptionStatus,
  isPackableAsset,
  roleCanPack,
  type PackingWriteResponse,
  type PublicAsset,
  type PublicTote,
} from '@inventohub/shared';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';
import { SearchablePickList } from './SearchablePickList';

function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];
}

export function PackingEditor({
  packingPath,
  packedTotes,
  packedAssets,
  totes,
  assets,
  onPacked,
}: {
  packingPath: string;
  packedTotes: PublicTote[];
  packedAssets: PublicAsset[];
  totes: PublicTote[];
  assets: PublicAsset[];
  onPacked: (body: PackingWriteResponse) => void;
}): JSX.Element {
  const { user } = useAuth();
  const canPack = user ? roleCanPack(user.role) : false;
  const [toteIds, setToteIds] = useState(packedTotes.map((tote) => tote.id));
  const [assetIds, setAssetIds] = useState(
    packedAssets.filter(isPackableAsset).map((asset) => asset.id),
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const packableAssets = useMemo(() => assets.filter(isPackableAsset), [assets]);
  const packedMats = packedAssets.filter(isPackableAsset);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const body = await apiRequest<PackingWriteResponse>(packingPath, {
        method: 'PUT',
        body: { toteIds, assetIds },
      });
      onPacked(body);
      if (body.conflicts.length > 0) {
        setNotice(
          `Packed with overlapping-assignment warnings (${body.conflicts.map((row) => row.resourceLabel).join(', ')}).`,
        );
      } else {
        setNotice('Packing saved.');
      }
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save packing.');
    } finally {
      setBusy(false);
    }
  }

  if (!canPack) {
    return (
      <div className="card" data-testid="packing-readonly">
        <h2>Packing</h2>
        <p className="meta">Staff may read packing. Only admin and equipment admin pack.</p>
        <p>
          <strong>Totes:</strong>{' '}
          {packedTotes.length > 0 ? packedTotes.map((tote) => tote.number).join(', ') : 'None'}
        </p>
        <p>
          <strong>Mats:</strong>{' '}
          {packedMats.length > 0 ? packedMats.map((asset) => asset.serial).join(', ') : 'None'}
        </p>
      </div>
    );
  }

  return (
    <form className="card" onSubmit={(event) => void onSubmit(event)} data-testid="packing-form">
      <h2>Pack this trip</h2>
      <p className="meta">Packing a tote here does not change what lives in the tote.</p>
      {error ? <p className="status status--error">{error}</p> : null}
      {notice ? <p className="status status--ok">{notice}</p> : null}
      <SearchablePickList
        legend="Totes"
        items={totes.filter((tote) => tote.assignable)}
        queryPlaceholder="Tote number"
        testId="pack-totes"
        filter={(tote, query) => tote.number.toLowerCase().includes(query)}
        renderItem={(tote) => {
          const exceptions = exceptionAssetsInTote(tote.id, assets);
          return (
            <label className="check">
              <input
                type="checkbox"
                checked={toteIds.includes(tote.id)}
                onChange={() => setToteIds(toggleId(toteIds, tote.id))}
                data-testid={`pack-tote-${tote.number}`}
              />
              {tote.number}
              {exceptions.length > 0 ? (
                <span
                  className="status status--warn status--inline"
                  data-testid={`pack-tote-warning-${tote.number}`}
                >
                  Contains{' '}
                  {exceptions.map((asset) => `${asset.serial} (${asset.status})`).join(', ')}
                </span>
              ) : null}
            </label>
          );
        }}
      />
      <SearchablePickList
        legend="Mats"
        items={packableAssets}
        queryPlaceholder="Mat serial"
        testId="pack-assets"
        filter={(asset, query) =>
          asset.serial.toLowerCase().includes(query) || asset.type.toLowerCase().includes(query)
        }
        renderItem={(asset) => (
          <label className="check">
            <input
              type="checkbox"
              checked={assetIds.includes(asset.id)}
              onChange={() => setAssetIds(toggleId(assetIds, asset.id))}
              data-testid={`pack-asset-${asset.serial}`}
            />
            {asset.serial} ({asset.type})
            {isExceptionStatus(asset.status) ? (
              <span
                className="status status--warn status--inline"
                data-testid={`pack-asset-warning-${asset.serial}`}
              >
                {asset.status}
              </span>
            ) : null}
          </label>
        )}
      />
      <button type="submit" className="button" disabled={busy} data-testid="save-packing">
        Save packing
      </button>
    </form>
  );
}
